import React, { useState, useEffect } from 'react';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProductDetails = ({ product, onUpdate, onDelete, onGenerateQR, onDownloadQr, haspermission }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    product_code: '',
    description: '',
  });

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || '',
        product_code: product.product_code || '',
        description: product.description || '',
      });
    }
  }, [product]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Name is required';
    }
    if (!formData.product_code.trim()) {
      newErrors.product_code = 'Product Code is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saveLoading) return;

    if (validateForm()) {
      try {
        setSaveLoading(true);
        // Create FormData object to send all the data, including file upload

        // Send the form data to the backend (API)
        const response = await fetch(import.meta.env.VITE_APP_ADMINURL + '/api/product-data/' + product.id, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),  // Send FormData with the logo file
        });

        if (response.ok) {
          const data = await response.json();
          // console.log('User added successfully:', data);
          onUpdate(data);
          setIsEditing(false);
        } else {
          const error = await response.json();
          console.error('Error adding user:', error);
        }
      } catch (error) {
        console.error('Something went wrong:', error);
      } finally {
        setSaveLoading(false);
      }
    }

  };

  const handleCancel = () => {
    if (saveLoading) return;
    if (product) {
      setFormData({
        title: product.title || '',
        product_code: product.product_code || '',
        description: product.description || ''
      });
    }
    setIsEditing(false);
  };


  const getQRStatus = (status) => {
    if (status === 'generated') return { label: 'Generated', color: 'text-green-600 bg-green-50' };
    if (status === 'pending') return { label: 'Pending', color: 'text-yellow-600 bg-yellow-50' };
    return { label: 'Not Generated', color: 'text-gray-600 bg-gray-50' };
  };

  if (!product) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="text-center text-gray-500">
          <Icon name="Package" size={48} color="#d1d5db" className="mx-auto mb-4" />
          <p>Select a product to view details</p>
        </div>
      </div>
    );
  }
  // console.log(product)

  // const qrStatus = getQRStatus(product.qrCode);
  const qrcodeStatus = product?.batches.length > 0 ? 'generated' : '';
  const qrStatus = getQRStatus(qrcodeStatus);

  return (
    <div className="bg-surface rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">Product Details</h3>
          {haspermission('Create Product') &&
            <div className="flex space-x-2">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={saveLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    loading={saveLoading}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          }
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {!isEditing ? (
          // View Mode
          <div className="space-y-6">
            {/* Product Image */}
            <div className="text-center">
              <Image
                // src={'https://hiraadmin.opaldemo.com/'+product.image}
                src={`${import.meta.env.VITE_APP_ADMINURL}/${product.image}`}
                alt={product.title}
                className="object-cover h-16"
              />
            </div>

            {/* Product Info */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Product Name</label>
                <p className="text-sm text-text-primary">{product.title}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Product Code</label>
                <p className="text-sm text-text-primary">{product.product_code}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Client</label>
                <p className="text-sm text-text-primary">{product.users?.company_name}</p>
              </div>


              <div>
                {/* <label className="text-sm font-medium text-gray-500">QR Remaining / Used</label> */}
                <label className="text-sm font-medium text-gray-500">Total Batches</label>
                <div className="mt-1">
                  {product?.batches.length}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">QR Code Status</label>
                <div className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${qrStatus.color}`}>
                    {qrStatus.label}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-sm text-text-primary">{product.description}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="text-sm text-text-primary capitalize">{product.status}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3 pt-4 border-t border-border">
              {product.qrCode !== 'generated' && haspermission("Create QR Code") && (
                <Button
                  className="text-white"
                  variant="primary"
                  fullWidth
                  iconName="QrCode"
                  onClick={() => onGenerateQR(product.id)}
                >
                  Generate QR Code
                </Button>
              )}

              {haspermission('Export Data') &&
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Download"
                  onClick={() => onDownloadQr(product.id)}
                >
                  Download QR Code
                </Button>
              }
              {haspermission('Create Product') &&
                <Button
                  variant="danger"
                  fullWidth
                  iconName="Trash2"
                  loading={deleteLoading}
                  onClick={async () => {
                    if (deleteLoading) return;
                    try {
                      setDeleteLoading(true);
                      await onDelete(product.id);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }}
                >
                  Delete Product
                </Button>
              }
            </div>
          </div>
        ) : (
          // Edit Mode
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name
              </label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Code
              </label>
              <Input
                type="text"
                name="product_code"
                value={formData.product_code}
                onChange={handleInputChange}
                required
              />
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>

          </form>
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
